// Tenantra Inc. - Edit Report Entry Page
import { useEffect, useState } from 'react';
import { 
  ArrowLeft, Calendar, CheckCircle, AlertTriangle, Clock, XCircle,
  Loader2, DollarSign, FileText, ChevronDown
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { navigate } from '@/services/navigation';
import { TableSkeleton } from '@/components/dashboard/TableKit';
import api, { apiHelpers } from '@/services/api';

const PAYMENT_STATUS_OPTIONS = [
  { value: 'on_time', label: 'On Time', icon: CheckCircle, color: 'text-emerald-600' },
  { value: 'late', label: 'Late', icon: Clock, color: 'text-amber-600' },
  { value: 'partial', label: 'Partial', icon: AlertTriangle, color: 'text-amber-600' },
  { value: 'missed', label: 'Missed', icon: XCircle, color: 'text-red-600' }
];

export default function EditReportPage() {
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [report, setReport] = useState(null);

  const [formData, setFormData] = useState({
    payment_status: 'on_time',
    days_late: '',
    amount_owing: '',
    notes: ''
  });

  // Get report ID from URL
  const reportId = window.location.pathname.split('/')[3];

  useEffect(() => {
    if (reportId) {
      fetchReportDetails();
    }
  }, [reportId]);

  const fetchReportDetails = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/reports/${reportId}`);
      const data = response.data;
      setReport(data);
      setFormData({
        payment_status: data.payment?.status || 'on_time',
        days_late: data.payment?.days_late || '',
        amount_owing: data.payment?.amount_owing || '',
        notes: data.notes || ''
      });
    } catch (err) {
      toast.error(apiHelpers.handleError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setSubmitting(true);
      
      await api.put(`/reports/${reportId}`, {
        payment: {
          status: formData.payment_status,
          days_late: formData.days_late ? parseInt(formData.days_late) : null,
          amount_owing: formData.amount_owing ? parseFloat(formData.amount_owing) : null
        },
        notes: formData.notes
      });
      
      toast.success('Report entry updated!');
      navigate('/landlord/reports');
    } catch (err) {
      toast.error(apiHelpers.handleError(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <TableSkeleton rows={5} spans={[5, 3, 2, 2]} />;
  }

  if (!report) {
    return (
      <div className="text-center py-16">
        <FileText className="w-12 h-12 text-slate-400 mx-auto mb-4" />
        <h3 className="text-xl font-semibold text-slate-900 mb-2">Report Not Found</h3>
        <a href="/landlord/reports">
          <Button variant="outline">Back to Reports</Button>
        </a>
      </div>
    );
  }

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    // Parse as local date to avoid timezone shift
    const [year, month, day] = dateStr.split('T')[0].split('-');
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('en-CA', { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric' 
    });
  };

  return (
    <div className="max-w-2xl mx-auto space-y-8">
      {/* Header */}
      <div>
        <a 
          href="/landlord/reports" 
          className="inline-flex items-center gap-2 text-slate-500 hover:text-slate-800 transition-colors mb-4"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Reports
        </a>
        <h1 className="text-2xl font-bold text-slate-900">Edit Report Entry</h1>
        <p className="text-slate-600">
          {report.tenant_name} • {formatDate(report.report_period?.start)} — {formatDate(report.report_period?.end)}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Payment Status */}
        <div className="bg-white border border-slate-200 rounded-xl p-6">
          <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-teal-600" />
            Payment Status
          </h2>
          
          <div className="grid grid-cols-2 gap-3">
            {PAYMENT_STATUS_OPTIONS.map(option => {
              const Icon = option.icon;
              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => handleChange('payment_status', option.value)}
                  className={`p-4 rounded-xl border-2 text-left flex items-center gap-3 transition-all ${
                    formData.payment_status === option.value
                      ? 'border-teal-500 bg-teal-50'
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <Icon className={`w-5 h-5 ${option.color}`} />
                  <span className="font-medium text-slate-900">{option.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Additional Details */}
        {(formData.payment_status === 'late' || formData.payment_status === 'partial') && (
          <div className="bg-white border border-slate-200 rounded-xl p-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-4">Payment Details</h2>
            
            <div className="grid md:grid-cols-2 gap-4">
              {formData.payment_status === 'late' && (
                <div>
                  <Label htmlFor="days_late" className="text-slate-700">Days Late</Label>
                  <Input
                    id="days_late"
                    type="number"
                    min="1"
                    placeholder="e.g., 5"
                    value={formData.days_late}
                    onChange={(e) => handleChange('days_late', e.target.value)}
                    className="mt-1 bg-white border-slate-200 text-slate-900"
                  />
                </div>
              )}
              
              <div>
                <Label htmlFor="amount_owing" className="text-slate-700">Amount Owing ($CAD)</Label>
                <div className="relative mt-1">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="amount_owing"
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    value={formData.amount_owing}
                    onChange={(e) => handleChange('amount_owing', e.target.value)}
                    className="pl-9 bg-white border-slate-200 text-slate-900"
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Notes */}
        <div className="bg-white border border-slate-200 rounded-xl p-6">
          <h2 className="text-lg font-semibold text-slate-900 mb-4">Notes</h2>
          <textarea
            placeholder="Any additional notes about this payment period..."
            value={formData.notes}
            onChange={(e) => handleChange('notes', e.target.value)}
            rows={4}
            className="w-full px-3 py-2 bg-white border border-slate-200 rounded-md text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500/50"
          />
        </div>

        {/* Warning */}
        {report.is_disputed && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
              <div>
                <p className="text-amber-800 font-medium">This report is under dispute</p>
                <p className="text-amber-700 text-sm">Changes may be reviewed as part of the dispute process.</p>
              </div>
            </div>
          </div>
        )}

        {/* Submit */}
        <div className="flex items-center justify-end gap-4">
          <a href="/landlord/reports">
            <Button type="button" variant="outline" className="border-slate-200 text-slate-600">
              Cancel
            </Button>
          </a>
          <Button 
            type="submit" 
            disabled={submitting}
            className="bg-teal-600 hover:bg-teal-700 text-white font-semibold min-w-[140px]"
          >
            {submitting ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <>
                <CheckCircle className="w-5 h-5 mr-2" />
                Save Changes
              </>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
}
